import { ListItem, View } from 'tamagui'
import type { ComponentProps } from 'react'
import { TouchableOpacity, Text, StyleSheet } from 'react-native'
import AntDesign from '@expo/vector-icons/AntDesign'
import { BankAccountManagement } from '@/src/assets/icons/BankAccountManagement'
import { ClockIcon } from '@/src/assets/icons/ClockIcon'
import { GetLinkedExternalBankAccountsQuery } from '@api/mesa/graphql'
import { TrashIcon } from '@/src/assets/icons/trash'
import { mesaColorNames, typography } from '@/src/common/theme'
import { groupNames } from '@/tamagui.config'
import { BoxedIcon } from '@/src/design-system/BoxedIcon'
import type { BasePaymentMethod } from '@/src/experiences/card-payments/providePaymentMethods'
import { Plaid } from '@acre-rewards/lib'

const styles = StyleSheet.create({
  itemIconPendingContainer: {
    backgroundColor: mesaColorNames['semantic/bg/disabled #DADDDE'],
  },
  itemTextContainer: {
    flex: 8,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  itemName: {
    ...typography.S,
    color: mesaColorNames['global/grey/80 #2F3133'],
    marginBottom: 3,
  },
  itemLast4: {
    ...typography.XS,
    color: mesaColorNames['Greyscale/500 #6E7375'],
  },
  sectionTitle: {
    ...typography['XS Bold'],
    paddingHorizontal: 16,
    color: mesaColorNames['semantic/fg/subtle #898D8F'],
    paddingBottom: 6,
  },
  rightStyle: {
    width: 92,
  },
  addNewContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    cursor: 'pointer',
  },
  addNewText: {
    ...typography.S,
    marginHorizontal: 15,
    color: mesaColorNames['Mesa - Brand/Secondary #223843'],
  },
})

export function parseLinkedExternalBankAccounts({
  getLinkedExternalBankAccounts: { accounts },
}: GetLinkedExternalBankAccountsQuery) {
  return accounts.map(
    (account) =>
      ({
        ...account,
        last4: `${account.accountSubtype} - ${account.last4}`,
        name: account.bankName || `Bank - ${account.accountSubtype}`,
        key: account.highnoteExternalAccountId || account.plaidAccountId || account.accountName,
        products: account.products.map((legacyProduct) => Plaid.Products.enum[legacyProduct]),
        status: account.status ?? null,
        plaidAccountId: account.plaidAccountId ?? undefined,
        highnoteExternalAccountId: account.highnoteExternalAccountId ?? undefined,
      }) as const,
  ) satisfies BasePaymentMethod[]
}

function AccountListItemRow(props: ComponentProps<typeof ListItem>) {
  return (
    <ListItem
      // @ts-expect-error tamagui typescript issue?
      hoverStyle={{ backgroundColor: `${mesaColorNames['semantic/fg/subtle #898D8F']}20` }}
      animation="75ms"
      backgroundColor="transparent"
      paddingHorizontal={16}
      paddingVertical={10}
      flexDirection="row"
      gap={8}
      group={groupNames.accountListItemRow}
      {...props}
    />
  )
}

const ButtonIfOnPressElseView = ({
  children,
  onPress,
  style,
}: Pick<ComponentProps<typeof TouchableOpacity>, 'onPress' | 'children' | 'style'>) =>
  onPress ? (
    <TouchableOpacity style={[{ flexDirection: 'row', width: '100%' }, style]} onPress={onPress}>
      {children}
    </TouchableOpacity>
  ) : (
    <View style={[{ flexDirection: 'row', width: '100%' }, style]}>{children}</View>
  )

function SectionItem({
  account,
  onDeletePress,
  onVerifyPress = undefined,
}: {
  account: BasePaymentMethod
  onDeletePress: () => void
  onVerifyPress?: () => void
}) {
  return (
    <ButtonIfOnPressElseView
      style={{ minHeight: 40, alignItems: 'center', gap: 8 }}
      onPress={onVerifyPress}
    >
      <BoxedIcon
        icon={account.isVerified ? <BankAccountManagement /> : <ClockIcon />}
        style={!account.isVerified && styles.itemIconPendingContainer}
      />
      <View style={styles.itemTextContainer}>
        <Text style={styles.itemName}>{account.name}</Text>
        <Text style={styles.itemLast4}>{account.last4}</Text>
      </View>

      <View
        onPress={(e) => {
          e.stopPropagation()
          onDeletePress()
        }}
        style={{ justifyContent: 'center', alignItems: 'flex-end', cursor: 'pointer' }}
        width={40}
        height={40}
        opacity={0.5}
        animation="75ms"
        $group-accountListItemRow-hover={{ opacity: 1 }}
        pressStyle={{ opacity: 0.8 }}
        $group-accountListItemRow-press={{ opacity: 0.8 }}
      >
        <TrashIcon pathProps={{ stroke: mesaColorNames['semantic/fg/error #DB340B'] }} />
      </View>
    </ButtonIfOnPressElseView>
  )
}

const NoBankAccountsLinked = () => (
  <View style={{ padding: 20, alignItems: 'center' }}>
    <Text style={{ fontSize: 12, color: '#888' }}>No bank accounts linked yet.</Text>
  </View>
)

const VerifiedAccount = <Account extends BasePaymentMethod>({
  account,
  onDeletePress,
}: {
  account: Account
  onDeletePress: (parsedAccount: Account) => void
}) => (
  <AccountListItemRow>
    <SectionItem account={account} onDeletePress={() => onDeletePress(account)} />
  </AccountListItemRow>
)

const UnverifiedAccount = <Account extends BasePaymentMethod>({
  account,
  onDeletePress,
  onVerifyPress,
}: {
  account: Account
  onDeletePress: (parsedAccount: Account) => void
  onVerifyPress: (parsedAccount: Account) => void
}) => (
  <AccountListItemRow>
    <SectionItem
      account={account}
      onDeletePress={() => onDeletePress(account)}
      onVerifyPress={() => onVerifyPress(account)}
    />
  </AccountListItemRow>
)

function AccountListItem<Account extends BasePaymentMethod>({
  account,
  onDeletePress,
  onVerifyPress,
}: {
  account: Account
  onDeletePress: (parsedAccount: Account) => void
  onVerifyPress: (parsedAccount: Account) => void
}) {
  return account.isVerified ? (
    <VerifiedAccount account={account} onDeletePress={onDeletePress} />
  ) : (
    <UnverifiedAccount
      account={account}
      onDeletePress={onDeletePress}
      onVerifyPress={onVerifyPress}
    />
  )
}

export function AccountSection<Account extends BasePaymentMethod>({
  sectionName,
  accounts,
  onAddNewPress,
  onDeletePress,
  onVerifyPress,
}: {
  sectionName: string
  accounts?: Account[]
  onAddNewPress: () => void
  onDeletePress: (parsedAccount: Account) => void
  onVerifyPress: (parsedAccount: Account) => void
}) {
  return (
    <View>
      <Text style={styles.sectionTitle}>{sectionName}</Text>
      <View style={{ overflow: 'hidden' }}>
        {accounts?.length ? (
          accounts.map((account) => (
            <AccountListItem
              key={account.key}
              account={account}
              onDeletePress={onDeletePress}
              onVerifyPress={onVerifyPress}
            />
          ))
        ) : (
          <NoBankAccountsLinked />
        )}
        <AccountListItemRow
          onPress={onAddNewPress}
          style={styles.addNewContainer}
          pressStyle={{ opacity: 0.2 }}
          // @ts-expect-error tamagui typescript issue?
          paddingVertical={20}
        >
          {addNewAccountButtonIcon}
          {addNewAccountButtonText}
        </AccountListItemRow>
      </View>
    </View>
  )
}

export const addNewAccountButtonIcon = <AntDesign name="plus" size={18} />
export const addNewAccountButtonText = <Text style={styles.addNewText}>Add new</Text>
